import React from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@tanstack/react-query'
import { Play } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { bobAnalyseService } from '@/lib/services/bobAnalyseService'
import { formatDateTime } from '@/lib/utils/timeFormat'
import type {
  AvailableScan,
  BobAnalyseFormData,
  BobAnalyse,
} from '@/lib/services/bobAnalyseService'

interface BobAnalyseNewDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
  onAnalyseCreated?: (bobId: string) => void
  availableScans: AvailableScan[]
  existingBobAnalyses?: BobAnalyse[]
}

type CreationMode = 'with-scan' | 'empty' | 'copy'

const getRelativeDate = (date: Date): string => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)

  const checkDate = new Date(date)
  checkDate.setHours(0, 0, 0, 0)

  const time = date.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })

  if (checkDate.getTime() === today.getTime()) {
    return `vandaag om ${time}`
  } else if (checkDate.getTime() === yesterday.getTime()) {
    return `gisteren om ${time}`
  } else {
    return formatDateTime(date)
  }
}

export function BobAnalyseNewDialog({
  open,
  onOpenChange,
  onSuccess,
  onAnalyseCreated,
  availableScans,
  existingBobAnalyses = [],
}: BobAnalyseNewDialogProps) {
  const { t } = useTranslation()
  const [creationMode, setCreationMode] = React.useState<CreationMode>('with-scan')
  const [selectedScanId, setSelectedScanId] = React.useState<string>('')
  const [selectedBobToCopy, setSelectedBobToCopy] = React.useState<string>('')

  const createBobAnalyseMutation = useMutation({
    mutationFn: (data: BobAnalyseFormData) => bobAnalyseService.createBobAnalyse(data),
    onSuccess: async response => {
      // Close the dialog immediately
      onOpenChange(false)

      // Notify parent of the created analyse ID so it can be selected
      if (onAnalyseCreated) {
        onAnalyseCreated(response.id)
      }

      // Poll for the created BOB analysis
      const pollForResult = async () => {
        try {
          const bobAnalyse = await bobAnalyseService.getBobAnalyse(response.id)
          if (bobAnalyse.status === 'completed' || bobAnalyse.status === 'failed') {
            if (onSuccess) onSuccess()
          } else {
            // Continue polling
            setTimeout(pollForResult, 2000)
          }
        } catch (error) {
          console.error('Error polling BOB analysis:', error)
          setTimeout(pollForResult, 2000)
        }
      }

      pollForResult()
    },
    onError: error => {
      console.error('Error creating BOB analysis:', error)
    },
  })

  const handleCreateBob = () => {
    if (creationMode === 'with-scan' && !selectedScanId) {
      console.error('Please select a scan')
      return
    }

    if (creationMode === 'copy' && !selectedBobToCopy) {
      console.error('Please select a BOB analysis to copy')
      return
    }

    let data: BobAnalyseFormData = {}

    if (creationMode === 'with-scan') {
      data = { scanId: selectedScanId }
    } else if (creationMode === 'copy') {
      // Find the selected BOB analysis to copy
      const bobToCopy = existingBobAnalyses.find(bob => bob.id === selectedBobToCopy)
      if (bobToCopy) {
        data = {
          beeldvorming: bobToCopy.beeldvorming,
          oordeelsvorming: bobToCopy.oordeelsvorming,
          besluitvorming: bobToCopy.besluitvorming,
        }
      }
    }
    // else: empty mode - data remains {}

    createBobAnalyseMutation.mutate(data)
  }

  // Reset state when dialog opens
  React.useEffect(() => {
    if (open) {
      setCreationMode('with-scan')
      setSelectedScanId('')
      setSelectedBobToCopy('')
    }
  }, [open])

  const canCreateWithScan = creationMode === 'with-scan' && selectedScanId
  const canCreateEmpty = creationMode === 'empty'
  const canCreateCopy = creationMode === 'copy' && selectedBobToCopy
  const canCreate = canCreateWithScan || canCreateEmpty || canCreateCopy

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Nieuwe BOB-GOGME Analyse</DialogTitle>
          <DialogDescription>Kies hoe u de analyse wilt maken</DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-6">
          {/* Option 1: Select existing scan */}
          <div
            className="space-y-3 p-4 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-gray-300 transition-colors"
            onClick={() => setCreationMode('with-scan')}
          >
            <div className="flex items-center gap-3">
              <input
                type="radio"
                id="with-scan"
                name="creation-mode"
                value="with-scan"
                checked={creationMode === 'with-scan'}
                onChange={() => setCreationMode('with-scan')}
                className="w-4 h-4 text-blue-600 cursor-pointer"
              />
              <label
                htmlFor="with-scan"
                className="flex-1 font-medium text-foreground cursor-pointer"
              >
                Analyse met omgevingsbeeld
              </label>
            </div>

            {creationMode === 'with-scan' && (
              <div className="ml-7 space-y-2">
                <p className="text-sm text-muted-foreground">
                  Selecteer een omgevingsbeeld om analyse op te baseren
                </p>
                <Select
                  value={selectedScanId}
                  onValueChange={setSelectedScanId}
                  disabled={
                    createBobAnalyseMutation.isPending ||
                    !availableScans ||
                    availableScans.length === 0
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue
                      placeholder={
                        availableScans && availableScans.length > 0
                          ? t('bob.selecteerOmgevingsbeeld')
                          : t('bob.geenOmgevingsbeelden')
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {availableScans &&
                      availableScans.length > 0 &&
                      (() => {
                        // Find the newest scan
                        const newestScanId = availableScans.reduce((newest, current) => {
                          const newestDate = new Date(newest.createdAt || 0)
                          const currentDate = new Date(current.createdAt || 0)
                          return currentDate > newestDate ? current : newest
                        }).id

                        return availableScans.map(scan => {
                          const createdDate = scan.createdAt ? getRelativeDate(new Date(scan.createdAt)) : 'Onbekende datum'
                          const isNewest = scan.id === newestScanId

                          return (
                            <SelectItem key={scan.id} value={scan.id} className="py-2">
                              <div className="flex flex-col gap-2">
                                <div className="flex items-center gap-2">
                                  <span>Omgevingsbeeld van {createdDate}</span>
                                  {isNewest && (
                                    <span className="inline-flex items-center px-2 py-1 rounded-md bg-blue-100 text-xs text-blue-700 font-medium">
                                      Nieuwste
                                    </span>
                                  )}
                                </div>
                                {scan.createdBy && (
                                  <span className="text-xs text-muted-foreground">Door: {scan.createdBy}</span>
                                )}
                              </div>
                            </SelectItem>
                          )
                        })
                      })()}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Option 2: Copy existing */}
          <div
            className="space-y-3 p-4 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-gray-300 transition-colors"
            onClick={() => setCreationMode('copy')}
          >
            <div className="flex items-center gap-3">
              <input
                type="radio"
                id="copy"
                name="creation-mode"
                value="copy"
                checked={creationMode === 'copy'}
                onChange={() => setCreationMode('copy')}
                className="w-4 h-4 text-blue-600 cursor-pointer"
              />
              <label htmlFor="copy" className="flex-1 font-medium text-foreground cursor-pointer">
                Kopieer bestaande analyse
              </label>
            </div>

            {creationMode === 'copy' && (
              <div className="ml-7 space-y-2">
                <p className="text-sm text-muted-foreground">
                  Selecteer een bestaande analyse om te kopiëren
                </p>
                <Select
                  value={selectedBobToCopy}
                  onValueChange={setSelectedBobToCopy}
                  disabled={
                    createBobAnalyseMutation.isPending ||
                    !existingBobAnalyses ||
                    existingBobAnalyses.length === 0
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue
                      placeholder={
                        existingBobAnalyses && existingBobAnalyses.length > 0
                          ? 'Selecteer analyse'
                          : 'Geen analyses beschikbaar'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {existingBobAnalyses &&
                      existingBobAnalyses.length > 0 &&
                      existingBobAnalyses.map((bob, index) => {
                        const createdDate = new Date(bob.createdAt)
                        const formattedDateTime = formatDateTime(createdDate)
                        const analyseNumber = index + 1

                        return (
                          <SelectItem key={bob.id} value={bob.id} className="py-2">
                            <div className="flex items-center gap-2">
                              <span className="text-sm">
                                Strategie {analyseNumber} | {formattedDateTime}
                              </span>
                            </div>
                          </SelectItem>
                        )
                      })}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Option 3: Create empty */}
          <div
            className="space-y-3 p-4 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-gray-300 transition-colors"
            onClick={() => setCreationMode('empty')}
          >
            <div className="flex items-center gap-3">
              <input
                type="radio"
                id="empty"
                name="creation-mode"
                value="empty"
                checked={creationMode === 'empty'}
                onChange={() => setCreationMode('empty')}
                className="w-4 h-4 text-blue-600 cursor-pointer"
              />
              <label htmlFor="empty" className="flex-1 font-medium text-foreground cursor-pointer">
                Lege analyse
              </label>
            </div>

            {creationMode === 'empty' && (
              <div className="ml-7">
                <p className="text-sm text-muted-foreground">
                  Maak een lege analyse die u handmatig kunt invullen
                </p>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={createBobAnalyseMutation.isPending}
          >
            {t('common.annuleren')}
          </Button>
          <Button
            onClick={handleCreateBob}
            disabled={createBobAnalyseMutation.isPending || !canCreate}
          >
            <Play className="mr-2 h-4 w-4" />
            {createBobAnalyseMutation.isPending ? t('bob.genereren') : 'Aanmaken'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
